import {
  DateRange,
  ListAlt,
  People,
  Person,
  SwapVert,
} from '@mui/icons-material';
import {
  Box,
  Checkbox,
  FormGroup,
  Radio,
  RadioGroup,
  Stack,
} from '@mui/material';
import {
  DialogActionsWithButtons,
  DialogContent,
  formatDate,
  Show,
  Tile,
  useIsMobile,
} from '@side/pantry';
import { useAtomValue } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import { snakeCase } from 'lodash';
import { useEffect } from 'react';
import { DateRangePicker } from '@/components/DateRangePicker';
import { FiltersDialog as CommonFiltersDialog } from '@/components/FiltersDialog';
import { FilterHeading } from '@/views/Interactions/components/SearchBar/FiltersButton/FiltersDialog/FilterHeading';
import { TeamsFilter } from '@/views/Interactions/components/SearchBar/FiltersButton/FiltersDialog/PeopleFilter';
import { TransactionStatusFilter } from '@/views/Interactions/components/SearchBar/FiltersButton/FiltersDialog/TransactionStatusFilter';
import {
  type InteractionFilter,
  interactionsEndDateFilterState,
  interactionsFiltersCountState,
  interactionsFiltersState,
  useClearEndDateFilter,
  useSetEndDateFilterState,
  useToggleFilter,
} from '@/views/Interactions/state/filters';
import {
  TrackReferralsEvent,
  useTrackReferralsAnalytics,
} from '$analytics/useTrackAnalytics/domains/referrals';

type FiltersDialogProps = {
  isOpen: boolean;
  onChangeSort: (option: string) => void;
  onClose?: () => void;
  selectedAgreementStatuses: InteractionFilter[];
  selectedClientTypes: InteractionFilter[];
  selectedSort: string;
};

const agreementStatuses: InteractionFilter[] = [
  { label: 'Not Signed', value: 'agreementStatus:sent' },
  { label: 'Partially Signed', value: 'agreementStatus:partiallySigned' },
  { label: 'Signed', value: 'agreementStatus:completed' },
];

const clientTypes: InteractionFilter[] = [
  { label: 'Buyer', value: 'referralClientType:referralBuyer' },
  { label: 'Seller', value: 'referralClientType:referralSeller' },
  { label: 'Landlord', value: 'referralClientType:referralLandlord' },
  { label: 'Tenant', value: 'referralClientType:referralTenant' },
];

export function FiltersDialog(props: FiltersDialogProps) {
  const {
    isOpen,
    onChangeSort,
    onClose,
    selectedAgreementStatuses,
    selectedClientTypes,
    selectedSort,
  } = props;

  const isMobile = useIsMobile('md');

  const stateKey = 'referral';
  const filterCount = useAtomValue(interactionsFiltersCountState(stateKey));
  const resetFilters = useResetAtom(interactionsFiltersState(stateKey));
  const toggleFilter = useToggleFilter(stateKey);

  // End Date filter
  const { fromDate, toDate } = useAtomValue(
    interactionsEndDateFilterState(stateKey),
  );
  const setEndDateFilter = useSetEndDateFilterState(stateKey);
  const clearEndDateFilter = useClearEndDateFilter(stateKey);

  const selectedClientTypesLabels = selectedClientTypes.map(
    ({ label }) => label,
  );

  const trackEvent = useTrackReferralsAnalytics();

  const handleClearAll = () => {
    trackEvent(TrackReferralsEvent.ClickClearAllFilters);
    resetFilters();
    clearEndDateFilter();
  };

  useEffect(() => {
    if (isOpen) {
      trackEvent(TrackReferralsEvent.FilterModalMount);
    }
  }, [isOpen, trackEvent]);

  const handleClose = (eventTarget: string) => {
    trackEvent(TrackReferralsEvent.FilterModalUnmount, { eventTarget });
    onClose?.();
  };

  return (
    <CommonFiltersDialog
      onClose={() => handleClose('background')}
      open={isOpen}
    >
      <DialogContent
        onClose={() => handleClose('close_button')}
        title="Filters"
      >
        <Stack sx={{ gap: 4, mt: 1.5 }}>
          <Show when={isMobile}>
            <Box>
              <FilterHeading icon={SwapVert} label="Sort By" />

              <RadioGroup
                aria-label="Sort By"
                onChange={(e, value) => {
                  onChangeSort(value);
                  trackEvent(TrackReferralsEvent.UpdateFilter, {
                    filterSection: 'sort_by',
                    filterValue: snakeCase(
                      e.target.labels?.[0].innerText ?? 'unknown',
                    ),
                    task: 'select',
                  });
                }}
                sx={{
                  gap: 2,
                  '& label': {
                    marginLeft: 0,
                    marginRight: 0,
                  },
                }}
                value={selectedSort}
              >
                <Tile
                  control={<Radio />}
                  label="Start date"
                  value="startDate:desc"
                />
                <Tile
                  control={<Radio />}
                  label="End date"
                  value="endDate:asc"
                />
                <Tile
                  control={<Radio />}
                  label="Transacting Agent"
                  value="transactingAgent:asc"
                />
                <Tile
                  control={<Radio />}
                  label="Referring Agent"
                  value="referringAgent:asc"
                />
              </RadioGroup>
            </Box>
          </Show>

          <Box>
            <FilterHeading icon={Person} label="Client Type" />

            <FormGroup
              aria-label="Client Type"
              sx={{
                gap: 2,
                '& label': {
                  marginLeft: 0,
                  marginRight: 0,
                },
              }}
            >
              {clientTypes.map(({ label, value }) => {
                const checked = selectedClientTypesLabels.includes(label);

                return (
                  <Tile
                    key={value}
                    control={<Checkbox />}
                    checked={checked}
                    label={label}
                    onChange={() => {
                      toggleFilter({
                        label,
                        value,
                      });
                      trackEvent(TrackReferralsEvent.UpdateFilter, {
                        filterSection: 'client_type',
                        filterValue: snakeCase(label),
                        task: checked ? 'deselect' : 'select',
                      });
                    }}
                    value={value}
                  />
                );
              })}
            </FormGroup>
          </Box>

          <Box>
            <FilterHeading icon={ListAlt} label="Agreement Status" />

            <FormGroup
              aria-label="Agreement Status"
              sx={{
                gap: 2,
                '& label': {
                  marginLeft: 0,
                  marginRight: 0,
                },
              }}
            >
              {agreementStatuses.map((status) => {
                const { label, value } = status;
                const checked = selectedAgreementStatuses.some(
                  ({ value: filterValue }) => filterValue === value,
                );

                return (
                  <Tile
                    key={value}
                    label={label}
                    control={
                      <Checkbox
                        onClick={() => {
                          toggleFilter(status);
                          trackEvent(TrackReferralsEvent.UpdateFilter, {
                            filterSection: 'agreement_status',
                            filterValue: snakeCase(label),
                            task: checked ? 'deselect' : 'select',
                          });
                        }}
                      />
                    }
                    checked={checked}
                  />
                );
              })}
            </FormGroup>
          </Box>

          <TransactionStatusFilter type="referral" stateKey={stateKey} />

          <Box>
            <FilterHeading icon={DateRange} label="End Date Range" />
            <DateRangePicker
              FirstDatePicker={{
                onAccept: (newDate: Date | null) => {
                  setEndDateFilter({ fromDate: newDate });
                },
                onChange: (newDate: Date | null) => {
                  setEndDateFilter({ fromDate: newDate });
                  trackEvent(TrackReferralsEvent.UpdateFilter, {
                    filterSection: 'end_date_range_from',
                    filterValue: newDate ? formatDate(newDate, 'longDate') : '',
                    task: 'select',
                  });
                },
                slotProps: {
                  textField: {
                    id: 'end-date-range-from',
                    label: 'From',
                  },
                },
                value: fromDate ? new Date(fromDate) : null,
              }}
              SecondDatePicker={{
                onAccept: (newDate: Date | null) => {
                  setEndDateFilter({ toDate: newDate });
                },
                onChange: (newDate: Date | null) => {
                  setEndDateFilter({ toDate: newDate });
                  trackEvent(TrackReferralsEvent.UpdateFilter, {
                    filterSection: 'end_date_range_to',
                    filterValue: newDate ? formatDate(newDate, 'longDate') : '',
                    task: 'select',
                  });
                },
                slotProps: {
                  textField: {
                    id: 'end-date-range-to',
                    label: 'To',
                  },
                },
                value: toDate ? new Date(toDate) : null,
              }}
            />
          </Box>

          <Box>
            <FilterHeading icon={People} label="Team" />
            <TeamsFilter type="referral" stateKey={stateKey} hideLabel />
          </Box>
        </Stack>
      </DialogContent>

      <DialogActionsWithButtons
        CancelButtonProps={{
          buttonText: 'Clear All',
          disabled: filterCount === 0,
          onClick: handleClearAll,
        }}
        SubmitButtonProps={{
          defaultText: 'Done',
          onClick: () => handleClose('done_button'),
        }}
      />
    </CommonFiltersDialog>
  );
}
